import { CurrencyCode } from "@asmbl/shared/constants";
import { Currency } from "@asmbl/shared/currency";
import { Comparator, ComparatorWithOrder } from "@asmbl/shared/sort";
import { cx } from "@emotion/css";
import { TableCell, TableHead, TableRow } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import { ComponentType } from "react";
import { CellProps, Column, ColumnInstance } from "react-table";
import {
  CondensedTable_participant as Participant,
  CondensedTable_position as Position,
} from "src/__generated__/graphql";

import { SortableTableHeaderCell } from "src/components/SortableTable";
import { GRAY_4, GRAY_6, WHITE } from "src/theme";
import { CondensedTableActionsCell2 as CondensedTableActionsCell } from "../Cells/CondensedTableActionsCell2";
import { CondensedTableActivityCell2 as CondensedTableActivityCell } from "../Cells/CondensedTableActivityCell2";
import { CondensedTableLastActivityCell } from "../Cells/CondensedTableLastActivityCell";
import { CondensedTableNameCell2 as CondensedTableNameCell } from "../Cells/CondensedTableNameCell2";
import {
  LARGE_COL_WIDTH,
  SMALL_COL_WIDTH,
  XLG_COL_WIDTH,
} from "../Cells/dimensions";
import { useBulkActionsData } from "../Contexts/BulkActionsContext";
import { usePagination } from "../Contexts/PaginationContext";
import { useTableData } from "../Contexts/TableDataContext2";

export type ColumnComponent2 = ComponentType<CellProps<Participant>> & {
  id: string;
  column: Column<Participant>;
  sortable?: boolean;
  ordering: (data: {
    currencies: Map<CurrencyCode, Currency>;
    defaultCurrencyCode: CurrencyCode;
    defaultCurrency: Currency;
    availablePositions: Position[];
    workingHoursPerYear: number | undefined;
  }) => Comparator<Participant> | ComparatorWithOrder<Participant>;
};

const useStyles = makeStyles((theme) => ({
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: WHITE,
  },
  headerCell: {
    border: `1px solid ${GRAY_6}`,
    borderLeft: "none",
    borderCollapse: "collapse",
    textTransform: "uppercase",
    backgroundColor: WHITE,
    color: GRAY_4,
    fontWeight: 700,
    fontSize: "10px",
    lineHeight: "100%",
    textAlign: "left",
    padding: theme.spacing(0, 1.5),
  },
  nameCell: {
    left: 0,
  },
  actionCell: {
    position: "sticky",
    left: XLG_COL_WIDTH, // account for the name column
  },
  activityCell: {
    position: "sticky",
    // left: account for the name + action columns
    left: XLG_COL_WIDTH + LARGE_COL_WIDTH,
  },
  viewOnlyActivityCell: {
    position: "sticky",
    // left: account for the name columns
    left: XLG_COL_WIDTH,
  },
  lastActivityCell: {
    position: "sticky",
    // left: account for the name, action & activity columns
    left: XLG_COL_WIDTH + LARGE_COL_WIDTH + SMALL_COL_WIDTH,
    boxShadow: "1px 0px 0px 0px rgba(10, 36, 64, 0.1)",
    borderRight: `1px solid ${GRAY_6}`,
  },
  viewOnlyLastActivityCell: {
    position: "sticky",
    // left: account for the name + activity columns
    left: XLG_COL_WIDTH + SMALL_COL_WIDTH,
    boxShadow: "1px 0px 0px 0px rgba(10, 36, 64, 0.1)",
    borderRight: `1px solid ${GRAY_6}`,
  },
}));

type Props = {
  headers: ColumnInstance<Participant>[];
};

export function CondensedTableInnerHeader({ headers }: Props): JSX.Element {
  const classes = useStyles();
  const { sortBy, setSortBy, sortDir, setSortDir } = usePagination();
  const { clearSelectedParticipants } = useBulkActionsData();
  const { isViewOnly } = useTableData();

  const onChangeSort = (property: string) => {
    setSortBy(property);
    setSortDir(property === sortBy && sortDir === "asc" ? "desc" : "asc");

    clearSelectedParticipants();
  };

  return (
    <TableHead>
      <TableRow
        className={classes.stickyHeader}
        data-intercom-target={"table-header-thing"}
      >
        {headers.map((header) => {
          if (!header.isVisible) {
            return null;
          }

          const headerClasses = cx(classes.headerCell, {
            [classes.stickyHeader]:
              header.id === CondensedTableNameCell.id ||
              header.id === CondensedTableActionsCell.id ||
              header.id === CondensedTableActivityCell.id ||
              header.id === CondensedTableLastActivityCell.id,

            [classes.nameCell]: header.id === CondensedTableNameCell.id,

            [classes.actionCell]: header.id === CondensedTableActionsCell.id,

            [classes.activityCell]:
              header.id === CondensedTableActivityCell.id && !isViewOnly,
            [classes.viewOnlyActivityCell]:
              header.id === CondensedTableActivityCell.id && isViewOnly,
            [classes.lastActivityCell]:
              header.id === CondensedTableLastActivityCell.id && !isViewOnly,
            [classes.viewOnlyLastActivityCell]:
              header.id === CondensedTableLastActivityCell.id && isViewOnly,
          });

          return header.defaultCanSort === false ? (
            <TableCell
              {...header.getHeaderProps({
                style: {
                  maxWidth: header.width,
                  minWidth: header.width,
                  width: header.width,
                  fontSize: ".6875rem",
                },
              })}
              className={headerClasses}
            >
              {header.Header?.toString()}
            </TableCell>
          ) : (
            <SortableTableHeaderCell<Record<string, unknown>>
              cellTitle={header.Header?.toString()}
              orderByField={header.id}
              order={sortDir}
              isSelected={sortBy === header.id}
              handleRequestSort={onChangeSort}
              data-intercom-target={"plan-" + header.id}
              className={headerClasses}
              {...header.getHeaderProps({
                style: {
                  maxWidth: header.width,
                  minWidth: header.width,
                  width: header.width,
                },
              })}
              key={header.getHeaderProps().key}
              noWrap={false}
              align="left"
            />
          );
        })}
      </TableRow>
    </TableHead>
  );
}
